import { CheckCircle2, Globe, KeyRound, RefreshCw, Server, XCircle } from 'lucide-react';
import type { ReactNode } from 'react';
import { useMutation } from '@tanstack/react-query';
import { ApiError, api } from '../../lib/api.js';
import { Badge, Button, Field, Input, Panel, useToast } from '../../ui/index.js';

/**
 * One MCP server's connection, configured the same way as every other.
 *
 * THE POINT OF THIS COMPONENT is that there is only one of it. The RAG
 * server got a hand-written form; adding the Graph server produced a second
 * one; VP, Egnyte, Fulcrum, GIS and JSM would have produced five more, each
 * free to word its fields slightly differently and forget a detail the
 * others had. Adding a server is now an entry in `MCP_SERVERS`.
 *
 * Each server's credentials are its own — nothing is shared between them but
 * the shape and this code.
 *
 * `extra` is for the genuinely server-specific: the RAG server has an
 * environment label and a second credential for its push plane, and pushing
 * those into every server's form to avoid a prop would be worse than the
 * prop.
 */

type Source = 'platform' | 'environment' | 'unset';

export interface McpServerView {
  key: string;
  label: string;
  purpose: string;
  placeholder: string;
  whenAbsent: string;
  baseUrl: { value: string; source: Source };
  clientId: { value: string; source: Source };
  clientSecret: { set: boolean; hint: string; source: Source };
  tokenUrl: { value: string; source: Source };
  enabled: boolean;
  canConnect: boolean;
  /**
   * Only the directory has this: whether the Azure AD app-only credential
   * exists in the environment. The fields on this screen get us TO the MCP
   * server; this is the token it forwards to Graph to get an answer back.
   * Undefined for every other server, which needs no second credential.
   */
  appCredential?: boolean;
}

/**
 * "Get a new token".
 *
 * An MCP server keeps the tokens it issued in memory, so a restart forgets
 * every one while Tributary still holds its own. The settings are right and
 * a fresh token works, but the cached one is refused with a 401. Tributary
 * now retries on a new token by itself when that happens; this is the same
 * thing on demand, for every process, so nobody has to wait for a redeploy.
 */
function NewToken({ server }: { server: McpServerView }) {
  const toast = useToast();
  const renew = useMutation({
    mutationFn: () => api.post(`/api/v1/platform/mcp-servers/${server.key}/new-token`, {}),
    onSuccess: () =>
      toast.success(
        'Old tokens dropped',
        `Every part of Tributary gets a new ${server.label.toLowerCase()} token on its next call, within 30 seconds.`,
      ),
    onError: (e) => toast.error('Could not drop the tokens', e instanceof ApiError ? e.message : undefined),
  });
  return (
    <Button
      type="button"
      size="sm"
      variant="ghost"
      loading={renew.isPending}
      onClick={() => renew.mutate()}
      title="Use when the server was restarted and now refuses Tributary with a 401"
    >
      <RefreshCw aria-hidden className="h-3.5 w-3.5" />
      Get a new token
    </Button>
  );
}

function Origin({ source }: { source: Source }) {
  if (source === 'unset') return null;
  const env = source === 'environment';
  return (
    <Badge tone={env ? 'neutral' : 'info'}>{env ? 'from environment' : 'set here'}</Badge>
  );
}

export function McpServerPanel({
  server,
  value,
  onChange,
  onSave,
  dirty,
  saving,
  extra,
  footer,
}: {
  server: McpServerView;
  /** Current text for one field, which may be unsaved. */
  value: (field: string) => string;
  onChange: (field: string, next: string) => void;
  onSave: () => void;
  dirty: boolean;
  saving: boolean;
  extra?: ReactNode;
  footer?: ReactNode;
}) {
  return (
    <Panel
      title={server.label}
      subtitle={server.purpose}
      action={
        <Button onClick={onSave} disabled={!dirty} loading={saving}>
          Save
        </Button>
      }
      footer={
        <div className="space-y-1.5 text-2xs leading-relaxed text-ink-tertiary">
          {/* What happens when it is not set up, in this server's own terms.
              An empty field otherwise reads as something broken. */}
          <p>{server.whenAbsent}</p>
          <p>
            The secret is write-only: it is stored so the server can present it and never
            returned once saved. Leave it empty to keep the current value.
          </p>
          {footer}
        </div>
      }
    >
      <div className="mb-4 flex flex-wrap items-center gap-1.5">
        {server.enabled ? (
          <Badge tone="ok" icon={<CheckCircle2 />}>
            configured
          </Badge>
        ) : (
          <Badge tone="neutral" icon={<XCircle />}>
            not configured
          </Badge>
        )}
        {/* Configured and able to connect are different claims. A server with
            an address but no credentials looks set up and answers 401. */}
        <Badge tone={server.canConnect ? 'ok' : 'warn'} icon={<Server />}>
          {server.canConnect ? 'can authenticate' : 'no credentials'}
        </Badge>
        {server.enabled && !server.canConnect && (
          <Badge tone="neutral" icon={<Globe />}>
            will connect anonymously
          </Badge>
        )}
        {/* TWO GREEN BADGES OVER SOMETHING THAT CANNOT ANSWER.
            Reaching the directory server and being able to read the
            directory are different credentials, and only the first is on
            this screen. Without this the panel reported "configured" and
            "can authenticate" while every lookup failed, and the reason was
            a footnote three lines further down. */}
        {server.appCredential === false && (
          <Badge tone="warn" icon={<KeyRound />}>
            no Azure AD credential
          </Badge>
        )}
        {server.canConnect && (
          <span className="ml-auto">
            <NewToken server={server} />
          </span>
        )}
      </div>

      <div className="space-y-3">
        <Field label="Server address" hint={`Where ${server.label.toLowerCase()} is listening.`}>
          <div className="flex items-center gap-2">
            <Input
              value={value('baseUrl')}
              onChange={(e) => onChange('baseUrl', e.target.value)}
              placeholder={server.placeholder}
              className="font-mono"
              aria-label={`${server.label} server address`}
            />
            <Origin source={server.baseUrl.source} />
          </div>
        </Field>

        <Field
          label="Token endpoint"
          hint="Where the client id and secret are exchanged for a bearer."
        >
          <div className="flex items-center gap-2">
            <Input
              value={value('tokenUrl')}
              onChange={(e) => onChange('tokenUrl', e.target.value)}
              placeholder={`${server.placeholder}/token`}
              className="font-mono"
              aria-label={`${server.label} token endpoint`}
            />
            <Origin source={server.tokenUrl.source} />
          </div>
        </Field>

        <Field label="Client id" hint="This server's own. Not shared with any other.">
          <div className="flex items-center gap-2">
            <Input
              value={value('clientId')}
              onChange={(e) => onChange('clientId', e.target.value)}
              className="font-mono"
              aria-label={`${server.label} client id`}
            />
            <Origin source={server.clientId.source} />
          </div>
        </Field>

        <Field
          label="Client secret"
          hint={
            server.clientSecret.set
              ? `Stored, ending ${server.clientSecret.hint}. Type a new one to replace it.`
              : 'Not set.'
          }
        >
          <div className="flex items-center gap-2">
            <Input
              type="password"
              value={value('clientSecret')}
              onChange={(e) => onChange('clientSecret', e.target.value)}
              placeholder={server.clientSecret.set ? '••••••••' : ''}
              className="font-mono"
              aria-label={`${server.label} client secret`}
            />
            <Origin source={server.clientSecret.source} />
          </div>
        </Field>

        {extra}
      </div>
    </Panel>
  );
}
